/*
 * File: DialogHeader.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Updated: 12/7/2025 (migrated from moment to dayjs)
 * Copyright (c) DrivingDoc 2025
 */
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import DriveCountdown from 'components/DriveCountdown/DriveCountdown';

import { useDriveOverviewDialogContext } from '../../context/DriveOverviewDialogContext';

// Enable required Day.js plugins
dayjs.extend(advancedFormat);

/**
 * Header component for the DriveOverviewDialog.
 *
 * @remarks
 * Must be used within DriveOverviewDialog (uses context).
 * Displays course/section info, formatted date/time, and a smart countdown ("Starts in 5m", "In progress", etc.).
 */
export default function DialogHeader() {
  const { period, onClose } = useDriveOverviewDialogContext();

  if (!period) {
    return null;
  }

  const start = dayjs(period.start);
  const end = dayjs(period.end);

  return (
    <DialogTitle sx={{ bgcolor: 'primary.lighter', py: 1.5 }}>
      {/* First Line: Course Title + Close Button */}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={0.5}>
        <Typography variant="h5" fontWeight="bold" color="primary.dark" sx={{ lineHeight: 1.2 }}>
          {period.course_section.course.title}
        </Typography>

        <IconButton aria-label="close" onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Second Line: Section • Date/Time • Countdown */}
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
        {/* Optional Section Title */}
        {period.course_section.title && period.course_section.title !== period.course_section.course.title && (
          <>
            <Typography variant="body1" color="text.secondary">
              {period.course_section.title}
            </Typography>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
          </>
        )}

        {/* Date & Time */}
        <Box display="flex" alignItems="center" gap={0.5}>
          <AccessTimeIcon fontSize="small" color="action" />
          <Typography variant="body1" color="text.secondary">
            {start.format('ddd, MMM D, YYYY')} · {start.format('h:mm A')} - {end.format('h:mm A')}
          </Typography>
        </Box>

        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />

        {/* Live Status — powered by DriveCountdown */}
        <DriveCountdown startTime={period.start} endTime={period.end} />
      </Box>
    </DialogTitle>
  );
}
